/**
 * Selenographic Coordinate Grid & Astrological Alignment Rings
 * Renders precise parallels, meridians, lunar equator, prime meridian,
 * and the lunar orbital ecliptic inclination plane.
 */

import * as THREE from 'three';

export class SelenographicGrid {
  constructor(radius = 5.01) {
    this.radius = radius;
    this.group = new THREE.Group();
    this.group.name = 'SelenographicGrid';
    this.visible = false;
    this.group.visible = false;

    this.buildGrid();
  }

  buildGrid() {
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x4a9eff,
      transparent: true,
      opacity: 0.35
    });

    const equatorMaterial = new THREE.LineBasicMaterial({
      color: 0x00f3ff,
      transparent: true,
      opacity: 0.85,
      linewidth: 2
    });

    const primeMeridianMaterial = new THREE.LineBasicMaterial({
      color: 0xffb703,
      transparent: true,
      opacity: 0.85,
      linewidth: 2
    });

    const limbMaterial = new THREE.LineBasicMaterial({
      color: 0xff006e,
      transparent: true,
      opacity: 0.65
    });

    // 1. Parallels (Latitude circles)
    const latitudes = [-75, -60, -45, -30, -15, 0, 15, 30, 45, 60, 75];
    latitudes.forEach(lat => {
      const phi = (90 - lat) * (Math.PI / 180);
      const r = this.radius * Math.sin(phi);
      const y = this.radius * Math.cos(phi);

      const segments = 96;
      const points = [];
      for (let i = 0; i <= segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        const x = r * Math.sin(theta);
        const z = r * Math.cos(theta);
        points.push(new THREE.Vector3(x, y, z));
      }

      const geom = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geom, lat === 0 ? equatorMaterial : lineMaterial);
      this.group.add(line);
    });

    // 2. Meridians (Longitude circles)
    for (let lon = -180; lon < 180; lon += 30) {
      const points = [];
      const segments = 96;
      for (let i = 0; i <= segments; i++) {
        const lat = -90 + (i / segments) * 180;
        const phi = (90 - lat) * (Math.PI / 180);
        const theta = (lon + 180) * (Math.PI / 180);

        const x = -(this.radius * Math.sin(phi) * Math.cos(theta));
        const z = this.radius * Math.sin(phi) * Math.sin(theta);
        const y = this.radius * Math.cos(phi);
        points.push(new THREE.Vector3(x, y, z));
      }

      const geom = new THREE.BufferGeometry().setFromPoints(points);
      let mat = lineMaterial;
      if (lon === 0) mat = primeMeridianMaterial;
      else if (Math.abs(lon) === 90) mat = limbMaterial; // Nearside / Farside boundary

      const line = new THREE.Line(geom, mat);
      this.group.add(line);
    }

    // 3. Astrological Orbital Ecliptic Ring (inclined by 5.145°)
    const eclipticPoints = [];
    const eclipticRadius = this.radius * 1.08;
    for (let i = 0; i <= 128; i++) {
      const angle = (i / 128) * Math.PI * 2;
      eclipticPoints.push(new THREE.Vector3(
        eclipticRadius * Math.cos(angle),
        0,
        eclipticRadius * Math.sin(angle)
      ));
    }
    const eclipticGeom = new THREE.BufferGeometry().setFromPoints(eclipticPoints);
    const eclipticMat = new THREE.LineDashedMaterial({
      color: 0xffd166,
      dashSize: 0.2,
      gapSize: 0.1,
      transparent: true,
      opacity: 0.6
    });
    const eclipticRing = new THREE.Line(eclipticGeom, eclipticMat);
    eclipticRing.computeLineDistances();
    // 5.145 degree lunar orbital inclination to ecliptic
    eclipticRing.rotation.z = (5.145 * Math.PI) / 180;
    this.group.add(eclipticRing);
  }

  setVisible(visible) {
    this.visible = visible;
    this.group.visible = visible;
  }
}
